'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSyncedCountdown } from '../../../utils/useSyncedCountdown';
import { Resource } from '../../resource';
import type { QuestionAnswerType } from '../Question.types';
import type { MultipleChoiceQuestionType } from '../multiple-choice-question/MultipleChoiceQuestion.types';
import type { MultipleChoiceAssessmentProps } from './MultipleChoiceAssessment.types';
import { AnswersStep } from './layout/steps/AnswersStep';
import { IndexStep } from './layout/steps/IndexStep';
import { PauseDialog } from './layout/steps/PauseDialog';
import { QuestionStep } from './layout/steps/QuestionStep';
import { useManualSubmission } from './useManualSubmission';

/**
 * The three screens the assessment moves between. One value rather than a flag each, so
 * "showing the index while also showing the answers" cannot be expressed at all.
 */
type AssessmentStep = 'question' | 'index' | 'answers';

/** What this sitting has changed about the questions it was given, keyed by question. */
type QuestionEdits = Record<string, Partial<MultipleChoiceQuestionType>>;

function toAnswers(
  questions: MultipleChoiceQuestionType[],
): QuestionAnswerType[] {
  return questions.map((question) => ({
    id: question.id,
    answerIndex: question.selectedOptionIndex,
    flagged: question.flagged,
  }));
}

function MultipleChoiceAssessment({
  style,
  className,
  timeInSeconds,
  questions,
  mode = 'interactive',
  onStartSubmitting,
  onScanAnswerSheet,
  isTranscribing = false,
  isSubmitting = false,
  instructions,
  onBookmark,
  onUnbookmark,
  onSubmit,
  canPause = true,
  onPause,
  onResume,
  onSyncTime,
  onDiscard,
  onClose,
  testId = 'ui-multiple-choice-assessment',
}: MultipleChoiceAssessmentProps) {
  const isManual = mode === 'manual';

  const [index, setIndex] = useState(0);
  const [step, setStep] = useState<AssessmentStep>(
    isTranscribing ? 'answers' : 'question',
  );

  const showAnswers = useCallback(() => setStep('answers'), []);

  const { isStarting: isStartingSubmitting, enter: enterAnswersStep } =
    useManualSubmission({
      alreadyClaimed: isTranscribing,
      onStartSubmitting,
      onEnter: showAnswers,
    });

  // Only what this sitting changed is kept, laid over whatever questions the props
  // currently carry. A questionnaire that arrives after the first render is picked up
  // without the answers given so far being thrown away, and `index` and `step` are left
  // alone: those belong to the sitting rather than to the props, and a second attempt is
  // a remount — pass a `key`.
  const [edits, setEdits] = useState<QuestionEdits>({});

  const questionnaire = useMemo(
    () =>
      questions.map((question) => {
        const edit = edits[question.id];

        return edit ? { ...question, ...edit } : question;
      }),
    [edits, questions],
  );

  const currentQuestion = questionnaire[index];
  const total = questionnaire.length;

  // Targeted by id rather than by position so a reply that lands after the user has
  // moved on still corrects the question it was about.
  const updateQuestion = useCallback(
    (id: string, changes: Partial<MultipleChoiceQuestionType>) => {
      setEdits((current) => ({
        ...current,
        [id]: { ...current[id], ...changes },
      }));
    },
    [],
  );

  // Running out of time in manual mode means the sitting is over, not the attempt:
  // submitting here would throw away answers that only exist on paper so far.
  const handleExpiry = useCallback(() => {
    if (isManual) {
      void enterAnswersStep(true);
      return;
    }

    onSubmit(toAnswers(questionnaire));
  }, [enterAnswersStep, isManual, onSubmit, questionnaire]);

  const countdown = useSyncedCountdown({
    // The sitting an attempt being transcribed had is over, and what is left to do is
    // not timed.
    timeInSeconds: isTranscribing ? 0 : timeInSeconds,
    onExpire: handleExpiry,
    onSyncTime,
    onPause: onPause && (() => onPause(toAnswers(questionnaire))),
    onResume,
  });

  const { status, secondsLeft, isSyncing, stop, pause, resume } = countdown;

  // The clock covers the sitting itself and nothing after it, so reaching the step where
  // what was answered on paper is transcribed stops it.
  useEffect(() => {
    if (step === 'answers') {
      stop();
    }
  }, [step, stop]);

  const handleSubmit = useCallback(() => {
    stop();
    onSubmit(toAnswers(questionnaire));
  }, [onSubmit, questionnaire, stop]);

  const isCompleted = index + 1 === total;

  const nextQuestion = useCallback(() => {
    setIndex((current) => Math.min(current + 1, total - 1));
  }, [total]);

  const previousQuestion = useCallback(() => {
    setIndex((current) => Math.max(current - 1, 0));
  }, []);

  const goQuestion = useCallback((newIndex: number) => {
    setIndex(newIndex);
    setStep('question');
  }, []);

  const showIndex = useCallback(() => setStep('index'), []);
  const showQuestion = useCallback(() => setStep('question'), []);

  const handleClose = useCallback(async () => {
    // Leaving an assessment that can be paused pauses it, so the answers given so far
    // are kept and the attempt can be picked up again instead of being left running.
    if (canPause && onPause && status === 'running') {
      try {
        await onPause(toAnswers(questionnaire));
      } catch {
        // Leaving regardless — the assessment is being abandoned either way.
      }
    }

    // Also what closes the pause dialog, which portals out of whatever is holding the
    // assessment and so would otherwise stay on screen at full opacity until that
    // finished closing and unmounted it.
    stop();
    onClose?.();
  }, [canPause, onClose, onPause, questionnaire, status, stop]);

  const close = onClose ? handleClose : undefined;

  const onBookmarkQuestion = useCallback(async () => {
    if (!currentQuestion) return;

    const { id, bookmarked } = currentQuestion;

    updateQuestion(id, { bookmarked: !bookmarked });

    try {
      if (bookmarked) {
        await onUnbookmark?.(id);
      } else {
        await onBookmark?.(id);
      }
    } catch {
      updateQuestion(id, { bookmarked });
    }
  }, [currentQuestion, onBookmark, onUnbookmark, updateQuestion]);

  const handleFlag = useCallback(() => {
    if (!currentQuestion) return;

    updateQuestion(currentQuestion.id, { flagged: !currentQuestion.flagged });
  }, [currentQuestion, updateQuestion]);

  const handleResponse = useCallback(
    (optionIndex: number) => {
      // The options are inert in manual mode, so this is unreachable from the UI — but
      // the rule belongs with the mode rather than only in the leaf that renders them.
      if (isManual || !currentQuestion) return;

      const previousIndex = currentQuestion.selectedOptionIndex;
      // Toggle off if clicking the same option, otherwise select the new option
      const newSelectedIndex = previousIndex === optionIndex ? 0 : optionIndex;

      updateQuestion(currentQuestion.id, {
        selectedOptionIndex: newSelectedIndex,
      });

      // Auto-advance if selecting a new option (not toggling off)
      if (newSelectedIndex > 0 && previousIndex === 0) {
        if (isCompleted) {
          setStep('index');
        } else {
          nextQuestion();
        }
      }
    },
    [currentQuestion, isCompleted, isManual, nextQuestion, updateQuestion],
  );

  const closeAction =
    !canPause && onDiscard
      ? 'none'
      : canPause && onPause
        ? 'pause-and-exit'
        : 'exit';

  return (
    <Resource style={style} className={className} testId={testId}>
      {step === 'answers' ? (
        <AnswersStep
          questions={questionnaire}
          isSubmitting={isSubmitting}
          onSelect={(id, answerIndex) =>
            updateQuestion(id, { selectedOptionIndex: answerIndex })
          }
          onSubmit={handleSubmit}
          onScanAnswerSheet={onScanAnswerSheet}
          onDiscard={onDiscard}
          onClose={close}
        />
      ) : step === 'index' ? (
        <IndexStep
          questions={questionnaire}
          secondsLeft={secondsLeft}
          isSubmitting={isSubmitting || isStartingSubmitting}
          submitLabel={isManual ? 'Rellenar respuestas' : undefined}
          onGoToQuestion={goQuestion}
          onSubmit={
            isManual ? () => void enterAnswersStep(false) : handleSubmit
          }
          onBack={showQuestion}
        />
      ) : (
        <QuestionStep
          question={currentQuestion}
          index={index}
          total={total}
          readOnly={isManual}
          secondsLeft={secondsLeft}
          instructions={instructions}
          closeAction={closeAction}
          pauseDisabled={!canPause || status !== 'running' || isSyncing}
          onResponse={handleResponse}
          onBookmark={onBookmark && onUnbookmark && onBookmarkQuestion}
          onFlag={handleFlag}
          onPause={pause}
          onShowIndex={showIndex}
          onPrevious={index > 0 ? previousQuestion : undefined}
          onNext={index < total - 1 ? nextQuestion : undefined}
          onDiscard={onDiscard}
          onClose={close}
        />
      )}
      <PauseDialog
        open={status === 'paused'}
        isSyncing={isSyncing}
        onResume={resume}
        onClose={close}
        testId={`${testId}-pause`}
      />
    </Resource>
  );
}

export { MultipleChoiceAssessment };
